import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MenuUnfoldOutlined } from '@ant-design/icons';
import { AppFooter, SideNav } from '../UI/nav';
import { UnifiedHeader } from '../Header/UnifiedHeader';
import { ChatPanel } from '../ChatPanel';
import { CommandHelp } from '../CommandHelp/CommandHelp';
import { SurfaceContextProvider } from '../../contexts/SurfaceContext';
import { useUser } from '../../contexts/UserContext';
import { useCommands } from '../../contexts/CommandContext';
import { useAppLayout } from './AppLayoutContext';
import {
  buildAppNavItems,
  DEFAULT_FOOTER_COLUMNS,
  hrefForNavKey,
  isMinimalChromePath,
  isOnboardingIntakePath,
  resolveActiveNavKey,
} from './appNavigationConfig';
import { useAppNavItems } from './useAppNavItems';
import styles from './AppShell.module.css';

export interface AppShellProps {
  children: React.ReactNode;
}

export const AppShell: React.FC<AppShellProps> = ({ children }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useUser();
  const { showCommandHelp, setShowCommandHelp, selectedCommand } = useCommands();
  const {
    sideNavCollapsed,
    sideNavHidden,
    sideNavMobileOpen,
    footerCollapsed,
    toggleSideNavCollapsed,
    toggleSideNavHidden,
    toggleSideNavMobile,
    closeSideNavMobile,
    toggleFooterCollapsed,
  } = useAppLayout();

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setShowCommandHelp(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [setShowCommandHelp]);

  const { navItems } = useAppNavItems();
  const activeKey = resolveActiveNavKey(pathname, navItems);
  const showSideNav = isAuthenticated && !isMinimalChromePath(pathname) && !isOnboardingIntakePath(pathname);
  const showFooter = isAuthenticated && !isMinimalChromePath(pathname) && !isOnboardingIntakePath(pathname);

  const handleNavigate = (key: string) => {
    const href = hrefForNavKey(key, navItems);
    if (href) {
      navigate(href);
      closeSideNavMobile();
    }
  };

  return (
    <SurfaceContextProvider>
      <div className={styles.shell}>
        <UnifiedHeader
          showNavigation={showSideNav}
          onMenuToggle={showSideNav ? toggleSideNavMobile : undefined}
        />
        <div className={styles.body}>
          {showSideNav && !sideNavHidden ? (
            <>
              {sideNavMobileOpen ? (
                <button
                  type="button"
                  className={styles.mobileBackdrop}
                  aria-label="Close navigation"
                  onClick={closeSideNavMobile}
                />
              ) : null}
              <SideNav
                items={navItems}
                activeKey={activeKey}
                collapsed={sideNavCollapsed}
                mobileOpen={sideNavMobileOpen}
                onToggleCollapse={toggleSideNavCollapsed}
                // Hide-sidebar remains implemented in SideNav + AppLayoutContext; omit wiring so only Collapse shows.
                onNavigate={handleNavigate}
              />
            </>
          ) : null}
          {showSideNav && sideNavHidden ? (
            <button
              type="button"
              className={styles.sideNavReveal}
              aria-label="Show navigation"
              title="Show navigation"
              onClick={toggleSideNavHidden}
            >
              <MenuUnfoldOutlined />
            </button>
          ) : null}
          <div className={styles.contentColumn}>
            <main className={styles.main}>{children}</main>
            {showFooter ? (
              <AppFooter
                compact
                collapsed={footerCollapsed}
                onToggleCollapsed={toggleFooterCollapsed}
                logo={
                  <img src="/krysalis-logo.svg" alt="Krysalis" style={{ height: 22, width: 'auto' }} />
                }
                columns={DEFAULT_FOOTER_COLUMNS}
                legal={`© ${new Date().getFullYear()} Krysalis`}
              />
            ) : null}
          </div>
          {showSideNav ? <ChatPanel /> : null}
        </div>
        {showCommandHelp ? (
          <CommandHelp
            visible={showCommandHelp}
            onClose={() => setShowCommandHelp(false)}
            selectedCommand={selectedCommand}
          />
        ) : null}
      </div>
    </SurfaceContextProvider>
  );
};

export default AppShell;
